import React, {useContext, useEffect, useMemo, useState} from 'react';
import "./SignDocCard.scss"
import {Button} from "../../Form/Button/Button";
import {getCardDesignValues} from "./SignDocCardLib";
import {SignDocTypes} from "../SignDocLib";
import {get} from "lodash";
import {I18nReactContext} from '../../../contexts/I18nContext'
import {getDateLocaleByLanguageCode, dateFormatIncludingTime} from '../../../contexts/ContextLib'
import {SignDocProgressSlider} from "../../../Utils/CardProgressSlider/CourseProgressSlider/SignDocProgressSlider";
import useSignDocTypes from '../../../Hooks/useSignDocTypes';
import {markDocAsViewedAPI, markSignDocAsCompleted} from '../../../Services/api';
import {dispatchSignDocCardEnterEvent} from '../../../Utils/SignDocs/SignDocs';
import {isQuestionnaireRedoableAPI} from '../../../Services/api';
import {isSignDocComplete} from '../SignDocLib';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';

/**
 * Determines the appropriate tooltip message for a disabled button based on the disable reason and SignDoc type
 * @param {object} params - The parameters object
 * @param {string} params.status - The status of the SignDoc (e.g., 'locked')
 * @param {boolean} params.isRedoable - Whether the SignDoc can be redone
 * @param {string} params.type - The type of SignDoc (e.g., SignDocTypes.SURVEY, SignDocTypes.EXAM)
 * @param {object} params.i18n - The i18n translation context
 * @param {boolean} params.isFrontalCourse - Whether this is a frontal course
 * @returns {string|null} The tooltip message or null if no tooltip should be shown
 */
const getDisabledButtonTooltip = ({ status, isRedoable, type, i18n, isFrontalCourse }) => {
    if (status === 'locked') {
        return i18n.translate('signDocCard.disabledButtonTooltips.locked');
    }

    if (isFrontalCourse) {
        return i18n.translate('signDocCard.disabledButtonTooltips.frontalCourseDisabled');
    }

    if (!isRedoable) {
        // Type-specific tooltip messages for when content is not redoable
        if (type === SignDocTypes.SURVEY) {
            return i18n.translate('signDocCard.disabledButtonTooltips.surveyNotRedoable');
        } else {
            // Generic message for other types
            return i18n.translate('signDocCard.disabledButtonTooltips.genericNotRedoable');
        }
    }

    return null; // No tooltip needed if button is not disabled
};

/**
 * SignDocCard displays a card for a SignDoc with title, image, description, and status.
 * Supports various statuses like locked, completed, active with appropriate visual indicators.
 * For course types (FRONTAL_COURSE, ONLINE_COURSE), displays session date and location/meeting link.
 */
export const SignDocCard = ({
    imgSrc,
    docName,
    finishByDate,
    progressPercent,
    viewed,
    signed,
    id,
    description,
    type,
    doNotForceCompletion,
    status = '',
    learnPathRootId,
    statusIcon, // Optional icon to display in the top-right corner
    forceShowEndDate = false,
    linkUrl, // URL for LINK type SignDocs
    additionalData, // UserSignDoc additionalData containing rejection info
    signDocAdditionalData, // SignDoc additionalData containing course-specific fields (sessionStartDate, location)
    overrideButtonText, // Optional: override the default button text (used for archived multiple versions)
    onOverrideButtonClick, // Optional: custom click handler for button (used for archived multiple versions)
    examScore, // Exam score from QuestionnaireSession (available for EXAM type)
}) => {
    const {i18n, language} = useContext(I18nReactContext);
    const { getMgmntImageIcon, getSignDocLinkPath } = useSignDocTypes();

    // Check if document is rejected (UPLOAD_DOCUMENT with 0% progress and rejection data)
    const rejectedAt = get(additionalData, 'rejectedAt', null);
    const isRejected = type === SignDocTypes.UPLOAD_DOCUMENT && progressPercent === 0 && rejectedAt;

    // Check if this is a course type (FRONTAL_COURSE or ONLINE_COURSE)
    const isCourseType = type === SignDocTypes.FRONTAL_COURSE || type === SignDocTypes.ONLINE_COURSE;
    const isFrontalCourse = type === SignDocTypes.FRONTAL_COURSE;
    const isOnlineCourse = type === SignDocTypes.ONLINE_COURSE;

    // Format finishByDate based on type - course types show date + time, others show date only
    const dateLocale = getDateLocaleByLanguageCode({language});
    const formattedFinishByDate = finishByDate
        ? isCourseType
            ? new Date(finishByDate).toLocaleString(dateLocale, dateFormatIncludingTime)
            : new Date(finishByDate).toLocaleDateString(dateLocale)
        : null;

    const {buttonColor, message, completionClass, buttonText} = getCardDesignValues({
        progressPercent,
        viewed,
        signed,
        finishByDate: formattedFinishByDate,
        type,
        i18n,
        isRejected
    })
    const [isRedoable, setIsRedoable] = useState(true)
    const emptyClick = () => {
    }

    const checkIfRedoable = async () => {
        if (type === SignDocTypes.SURVEY) {
            let isRedoable = await isQuestionnaireRedoableAPI({signDocId: id})
            setIsRedoable(isRedoable)
        }
    }

    useEffect(() => {
        checkIfRedoable()
    }, [type])

    // Store learning path relationship in local storage when a card is rendered with a learning path root ID
    const storeLearningPathRelationship = () => {
        if (id && learnPathRootId) {
            try {
                // Get existing mapping from local storage or initialize empty object
                const storedMapping = localStorage.getItem('signDocToLearningPathMap');
                const signDocToLearningPathMap = storedMapping ? JSON.parse(storedMapping) : {};

                // Update the mapping with current signDoc's learning path relationship
                signDocToLearningPathMap[id] = learnPathRootId;

                // Save updated mapping back to local storage
                localStorage.setItem('signDocToLearningPathMap', JSON.stringify(signDocToLearningPathMap));
            } catch (error) {
                console.error('Error storing learning path relationship in local storage:', error);
            }
        }
    }

    // Compute the path to open in a new tab
    const linkPath = useMemo(() => {
        return getSignDocLinkPath({ type, id });
    }, [type, id, getSignDocLinkPath]);

    // Handle card button click: store relationship then open in new tab
    const handleCardClick = async () => {
        storeLearningPathRelationship();
        // Special behavior for LINK type
        if (type === SignDocTypes.LINK && linkUrl) {
            // Always only open the external link URL
            window.open(linkUrl, '_blank', 'noopener,noreferrer');
            await markDocAsViewedAPI({ signDocId: id });

            // If not signed yet, automatically mark as completed
            if (!signed) {
                try {
                    await markSignDocAsCompleted({ signDocId: id, signatureBase64: '' });
                    // Dispatch event to notify other components (like MyOpenTasks) to refresh
                    dispatchSignDocCardEnterEvent({ signDocId: id, signDocType: type });
                } catch (error) {
                    console.error('Error marking link as completed:', error);
                }
            }
        } 
        // Special behavior for ONLINE_COURSE type - open meeting link directly
        else if (type === SignDocTypes.ONLINE_COURSE && signDocAdditionalData?.location) {
            // Open the meeting link URL directly
            const meetingLink = signDocAdditionalData.location;
            // Ensure the URL has a protocol
            const url = meetingLink.startsWith('http://') || meetingLink.startsWith('https://') 
                ? meetingLink 
                : `https://${meetingLink}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            
            // Dispatch event to notify other components to refresh
            setTimeout(() => {
                dispatchSignDocCardEnterEvent({ signDocId: id, signDocType: type });
            }, 1000);
        } 
        else {
            // Default behavior for other types
            window.open(linkPath, '_blank', 'noopener,noreferrer');
            setTimeout(() => {
                dispatchSignDocCardEnterEvent({ signDocId: id, signDocType: type });
            }, 1000);
        }
    };

    // Determine if image should have round class
    const imageClassName = type === SignDocTypes.REGULAR_COURSE ? 'signDocCardImage round' : 'signDocCardImage';

    // Determine if title should have small class
    const titleClassName = get(docName, 'length') > 20 ? 'signDocTitle small' : 'signDocTitle';

    // Check if button is disabled and get tooltip message
    // FRONTAL_COURSE cards are always disabled for users (attendance marked by admin only)
    // When override click handler is provided, button is always enabled (used for archived versions navigation)
    const isButtonDisabled = onOverrideButtonClick ? false : (status === 'locked' || !isRedoable || isFrontalCourse);
    const tooltipMessage = onOverrideButtonClick ? null : getDisabledButtonTooltip({ status, isRedoable, type, i18n, isFrontalCourse });

    // Determine button click handler - use override if provided
    const buttonClickHandler = onOverrideButtonClick || (isButtonDisabled ? emptyClick : handleCardClick);

    // Determine button text - use override if provided
    const finalButtonText = overrideButtonText || buttonText;

    // Create the button element
    const buttonElement = (
        <Button
            color={buttonColor}
            onClick={buttonClickHandler}
            disabled={isButtonDisabled}
            style={{width: '100%'}}
            text={finalButtonText}
        />
    );


    return (
        <div className={`SignDocCard ${doNotForceCompletion ? 'doNotForceCompletion' : ''} ${status} ${isRejected ? 'rejected' : ''}`}>
            {statusIcon && (
                <div className={`status-icon-wrapper`}>
                    {statusIcon}
                </div>
            )}
            {isRejected && (
                <div className="rejection-badge">
                    <WarningAmberIcon className="rejection-badge__icon" />
                    <span className="rejection-badge__text">{i18n.translate('signDocCard.rejected')}</span>
                </div>
            )}
            <div className={imageClassName}>
                <img src={imgSrc || getMgmntImageIcon({ type })} alt={docName} />
            </div>
            <div className="signDocCardContent">
                <div className={titleClassName} title={docName}>{docName}</div>
                <div className="signDocDescription" title={description}>{description}</div>

                {/* Course-specific information for FRONTAL_COURSE and ONLINE_COURSE */}
                {isCourseType && signDocAdditionalData && (
                    <div className="SignDocCard__course-info">
                        {/* Session Start Date */}
                        

                        {/* Location for FRONTAL_COURSE or Meeting Link indicator for ONLINE_COURSE */}
                        {signDocAdditionalData.location && (
                            <div className="SignDocCard__course-location">
                                {isFrontalCourse ? (
                                    <>
                                        <LocationOnIcon className="SignDocCard__course-icon" />
                                        <span title={signDocAdditionalData.location}>
                                            {signDocAdditionalData.location}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <LinkIcon className="SignDocCard__course-icon" />
                                        <span>{i18n.translate('courseFields.onlineSession')}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {/* Show exam score for completed exams, or "completed by admin" for score 101 */}
                {type === SignDocTypes.EXAM && examScore != null &&
                    isSignDocComplete({type, progressPercent, viewed, signed}) && (
                    <div className="SignDocCard__exam-score">
                        {parseInt(examScore, 10) === 101
                            ? i18n.translate('signDocCard.completedByAdmin')
                            : i18n.translate('signDocCard.examScore', { context: { score: parseInt(examScore, 10) } })
                        }
                    </div>
                )}

                {!doNotForceCompletion && (
                    <div className="signDocProgressWrapper">
                        <SignDocProgressSlider
                            progressPercent={progressPercent}
                            type={type}
                            signed={signed}
                            viewed={viewed}
                        />
                    </div>
                )}
                <div className="signDocButton">
                    {tooltipMessage ? (
                        <Tooltip title={tooltipMessage} placement="top">
                            <span style={{ width: '100%' }}>
                                {buttonElement}
                            </span>
                        </Tooltip>
                    ) : (
                        buttonElement
                    )}
                </div>
                {isCourseType ? (signDocAdditionalData?.sessionStartDate && (
                            <div className={`signDocEndDate ${completionClass}`}>
                                <CalendarTodayIcon className="SignDocCard__course-icon" />
                                {message}
                            </div>
                            )) : (!doNotForceCompletion || forceShowEndDate) && (
                    <div className={`signDocEndDate ${completionClass}`}>{message}</div>
                )}
            </div>
        </div>
    );
};

